// src/tours/tours.js — v1.4 TOUR-1 (D10): one guided tour per role, 5–8
// spotlight steps on data-tour anchors (sidebar items carry
// data-tour="nav:<path>"; the home pages carry their own). A release bumps
// TOUR_VERSION, so each tour shows once per user per version (the backend's
// UserTourState key "<role>@<version>"). Tenant copy is owner-branded — the
// landlord's name or "your landlord", never the platform.
import whatsNew from '../whatsNew.json';

export const TOUR_VERSION = whatsNew.version;
export const tourKey = (role) => `${role}@${TOUR_VERSION}`;

const at = (anchor) => `[data-tour="${anchor}"]`;
// Titles are short (one to three words); the description carries the sentence.
const step = (anchor, title, description) => ({ element: at(anchor), popover: { title, description } });

const LANDLORD_COMMON = {
  bell: step('bell', 'Your bell', 'New enquiries, tenant disputes and anything waiting on you land here.'),
  help: step('tour', 'Help', 'Open this tour again any time. Look for the ? on a page for a short explanation.'),
};

export const TOURS = {
  owner: [
    step('dashboard-tiles', 'Your portfolio', 'Every number is a link: click one to see exactly the rows it counts.'),
    step('nav:/properties', 'Properties', 'Open a property for its tabs. Charges lists its levies; Settings turns garbage, service charge, parking and the visitor log on or off.'),
    step('nav:/deposits', 'Deposits', 'Each deposit keeps a ledger of deductions and refunds. Answer a tenant\'s dispute from it.'),
    step('nav:/reports/rental-income', 'Rental income', 'Your monthly rental income, with the exclusions shown, ready to export.'),
    step('nav:/settings', 'Policies', 'In Settings: your notice period, late fees, repair targets and the deposit dispute window.'),
    step('nav:/staff', 'Staff', 'Invite managers and choose what each one may do.'),
    LANDLORD_COMMON.bell,
    LANDLORD_COMMON.help,
  ],
  manager: [
    step('dashboard-tiles', 'Your portfolio', 'Every number is a link to the list behind it.'),
    step('nav:/properties', 'Properties', 'The properties you manage, each with its tenants, money and maintenance.'),
    step('nav:/maintenance', 'Maintenance', 'Open requests with their repair targets; assign and track them here.'),
    step('nav:/payments', 'Payments', 'Record a payment or request one from a tenant, if your owner allows it.'),
    step('nav:/tenants', 'Tenants', 'Each tenant\'s balance, lease and payment history.'),
    LANDLORD_COMMON.bell,
  ],
  superadmin: [
    step('platform-tiles', 'The platform', 'Account lifecycle and billing figures; every tile opens its list.'),
    step('nav:/platform/organisations', 'Organisations', 'Every landlord account, filterable by status.'),
    step('nav:/platform/revenue', 'Revenue', 'Subscription and fee revenue.'),
    step('nav:/platform/payments', 'Payments', 'Stranded M-Pesa pushes and the reconciler\'s runs.'),
    step('nav:/platform/compliance', 'Tax rules', 'The tax configuration the rental income report reads.'),
    step('nav:/platform/system', 'System', 'Build, scheduled jobs and flags.'),
  ],
  // v1.4.1 C3 (D9): gate staff — their home is the visitor log.
  caretaker: [
    step('gate-checkin', 'Check in', 'Type or paste the code the guest shows, then Check in. The visit is logged.'),
    step('gate-walkin', 'Walk-ins', 'A guest without a pass: register them against the unit they are visiting.'),
    step('gate-log', 'The log', 'Who is expected today, who is inside and who has left. Check a guest out when they leave.'),
  ],
};

// The tenant tour names their landlord (W-1), so it is built per viewer.
export function tenantTour(brandName) {
  const landlord = brandName || 'your landlord';
  return [
    step('balance', 'What you owe', `Your balance with ${landlord}. Tap the amount for the invoices behind it, or Pay now for an M-Pesa prompt.`),
    step('ontime', 'On-time record', 'How many of your recent rents were paid on time. Create a rent payment record from here to show a future landlord.'),
    step('receipts', 'Receipts', 'Every payment\'s receipt, and a statement for any dates you choose.'),
    step('maintenance', 'Repairs', `Report an issue and follow it: who is on it and the target date ${landlord} works to.`),
    step('service-charge', 'Service charge', `What your service charge pays for: the budget ${landlord} set and your unit's share.`),
    step('deposit', 'Your deposit', 'What is still held, every deduction, and a way to dispute one.'),
    step('lease', 'Your lease', 'When it ends, any renewal offer, the signed PDF, and how to give notice.'),
    step('bell', 'Your bell', `Messages and notices from ${landlord} arrive here.`),
  ];
}

// While the property modules are dark for this owner (v1.4.1 D3: the flag,
// then the plan), the Properties step keeps its v1.4.0 wording — the tour
// never points at a tab the owner cannot see.
const OWNER_PROPERTIES_WITHOUT_MODULES = step('nav:/properties', 'Properties',
  'Open a property for its tabs: Overview, Tenants, Financial, Maintenance and Documents.');

export function tourSteps(role, { brandName, modulesAvailable = false } = {}) {
  if (role === 'tenant') return tenantTour(brandName);
  if (role === 'owner' && !modulesAvailable) {
    return TOURS.owner.map((s) => (s.element === OWNER_PROPERTIES_WITHOUT_MODULES.element ? OWNER_PROPERTIES_WITHOUT_MODULES : s));
  }
  return TOURS[role] || [];
}

TOURS.tenant = tenantTour('');
